import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import HelpButton from '../components/HelpButton';

export default function MessagesPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await api.listConversations();
      setConversations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function openConversation(convo) {
    if (convo.type === 'normal') {
      navigate(`/mensajes/con/${convo.other_user_id}`);
    } else if (convo.type === 'anon_sender') {
      navigate(`/mensajes/amigo-invisible/${convo.related_user_id}`);
    } else if (convo.type === 'anon_receiver') {
      navigate(`/mensajes/respondiendo/${convo.related_user_id}`);
    }
  }

  function timeAgo(dateStr) {
    const diff = (Date.now() - new Date(dateStr)) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
    return `${Math.floor(diff / 86400)} d`;
  }

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <button className="btn btn-ghost" onClick={() => navigate('/home')}>← Volver</button>
        <HelpButton title="💌 Mensajes">
          <p>Aquí puedes chatear en privado con cualquier miembro de la familia.</p>
          <p>También existe la opción <strong>"🎭 Al amigo invisible"</strong>: escribes con tu nombre, pero el mensaje llega de forma anónima a quien le toca regalarle a la persona que elijas.</p>
          <p>Ejemplo: "¿Ya has comprado el bolso de la lista de Carmen?" — así evitas comprar el mismo regalo, sin desvelar quién es el amigo invisible.</p>
          <p>Quien recibe ese mensaje puede responderte sin revelar su identidad.</p>
        </HelpButton>
      </div>

      <div style={styles.header}>
        <h2 style={{ color: 'var(--gold)', fontFamily: 'var(--font-display)' }}>💌 Mensajes</h2>
        <button className="btn btn-primary" onClick={() => setShowNewModal(true)}>+ Nuevo</button>
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Cargando...</p>
      ) : error ? (
        <p className="error-msg" style={{ textAlign: 'center' }}>⚠ {error}</p>
      ) : conversations.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
          No tienes ninguna conversación todavía.
        </p>
      ) : (
        <div style={styles.list}>
          {conversations.map(convo => (
            <div key={convo.key} className="card" style={styles.convoCard} onClick={() => openConversation(convo)}>
              <div style={styles.convoInfo}>
                <p style={styles.convoName}>{convo.display_name}</p>
                <p style={styles.convoTime}>hace {timeAgo(convo.last_at)}</p>
              </div>
              {convo.unread > 0 && <span style={styles.unreadBadge}>{convo.unread}</span>}
            </div>
          ))}
        </div>
      )}

      {showNewModal && (
        <NewMessageModal onClose={() => setShowNewModal(false)} onSent={() => { setShowNewModal(false); load(); }} />
      )}
    </div>
  );
}

function NewMessageModal({ onClose, onSent }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('normal'); // 'normal' | 'secret-santa'
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Reutilizamos listLetters solo para obtener la lista de personas (incluye display_name)
    api.listLetters(new Date().getFullYear()).then(data => {
      setUsers(data.map(l => ({ id: l.user_id, display_name: l.display_name })));
    }).catch(() => {});
  }, []);

  async function handleSend() {
    if (!selectedUser || !content.trim()) {
      setError('Elige una persona y escribe un mensaje');
      return;
    }
    setSending(true);
    setError('');
    try {
      if (mode === 'normal') {
        await api.sendMessage(parseInt(selectedUser), content.trim());
        onSent();
        navigate(`/mensajes/con/${selectedUser}`);
      } else {
        await api.sendToSecretSanta(parseInt(selectedUser), content.trim());
        onSent();
        navigate(`/mensajes/amigo-invisible/${selectedUser}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--gold)' }}>Nuevo mensaje</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.modeSelector}>
            <button
              className={mode === 'normal' ? 'btn btn-primary' : 'btn btn-ghost'}
              style={{ flex: 1 }}
              onClick={() => setMode('normal')}
            >
              💬 Mensaje directo
            </button>
            <button
              className={mode === 'secret-santa' ? 'btn btn-primary' : 'btn btn-ghost'}
              style={{ flex: 1 }}
              onClick={() => setMode('secret-santa')}
            >
              🎭 Al amigo invisible
            </button>
          </div>

          {mode === 'secret-santa' && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              Escribe de forma anónima a quien le toca regalarle a la persona que elijas. Tú apareces con tu nombre, pero quien te responda permanecerá en el anonimato.
            </p>
          )}

          <div>
            <label className="label">{mode === 'normal' ? 'Para' : '¿De quién es el regalo?'}</label>
            <select className="input" value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
              <option value="">Selecciona una persona...</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.display_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Mensaje</label>
            <textarea
              className="input"
              rows={3}
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder={mode === 'normal' ? 'Escribe tu mensaje...' : 'Ej: ¿Ya has comprado el bolso de su lista?'}
            />
          </div>

          {error && <p className="error-msg">⚠ {error}</p>}
        </div>

        <div style={styles.modalFooter}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '1rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  convoCard: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    cursor: 'pointer',
  },
  convoInfo: { flex: 1 },
  convoName: { fontWeight: 600, fontSize: '0.95rem' },
  convoTime: { color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: '0.15rem' },
  unreadBadge: {
    background: 'var(--gold)', color: '#1a0f35', borderRadius: '999px',
    minWidth: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.78rem', fontWeight: 700, padding: '0 0.4rem',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '500px',
    maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)',
  },
  closeBtn: { background: 'none', color: 'var(--text-muted)', fontSize: '1.2rem', cursor: 'pointer' },
  modalBody: { padding: '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem', flex: 1 },
  modalFooter: {
    padding: '1rem 1.5rem', borderTop: '1px solid var(--border)',
    display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
  },
  modeSelector: { display: 'flex', gap: '0.5rem' },
};
